import { z } from "zod";

export const ListZapsParams = z.object({
  limit: z
    .number()
    .default(10)
    .describe("Maximum number of Zaps to return (default: 10)"),
  sort_by: z
    .enum(["created_at", "updated_at", "title"])
    .default("updated_at")
    .describe("Field to sort results by (default: updated_at)"),
  sort_order: z
    .enum(["asc", "desc"])
    .default("desc")
    .describe("Sort order (asc or desc, default: desc)"),
  search: z
    .string()
    .optional()
    .describe("Search for Zaps by title and service title"),
});

type ListZapsParamsType = z.infer<typeof ListZapsParams>;

// This function will be called by the tool's 'execute' method.
// It needs to be adapted to run in the browser or call an API route.
export const listZapsExecute = async (rawArgs: any) => {
  let params: ListZapsParamsType;
  try {
    // Parse and apply defaults using the Zod schema
    params = ListZapsParams.parse(rawArgs || {}); // Ensure rawArgs is an object
  } catch (error) {
    console.error("Error parsing arguments for listZaps:", error);
    return {
      content: [
        {
          type: "text",
          text: `Error: Invalid arguments provided for list_zaps. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
        },
      ],
    };
  }

  // Now params will have defaults applied if properties were missing
  const queryParams = new URLSearchParams({
    limit: params.limit.toString(),
    sort_by: params.sort_by,
    sort_order: params.sort_order,
    ...(params.search && { search: params.search }),
  });

  // Call our internal proxy API route
  const proxyApiUrl = `/api/proxy/listZaps?${queryParams.toString()}`;

  try {
    const res = await fetch(proxyApiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    if (!res.ok) {
      const errorText = await res.text();
      // Log the error for more details during development
      console.error(
        `API proxy error (${res.status}) for URL ${proxyApiUrl}: ${errorText}`,
      );
      throw new Error(`API proxy error (${res.status})`);
    }

    const data = await res.json();
    return {
      content: [
        {
          type: "text",
          text: JSON.stringify(data, null, 2),
        },
      ],
    };
  } catch (error) {
    console.error("Error in listZapsExecute:", error);
    return {
      content: [
        {
          type: "text",
          text: `Error fetching Zaps: ${error instanceof Error ? error.message : String(error)}`,
        },
      ],
    };
  }
};

export const listZapsDescription =
  "Lists Zaps based on specified criteria. Allows filtering by search term, sorting, and pagination (limit).";
